<template>
  <div :class="['errpage', `errpage--${tone}`]">
    <div class="errpage__bg" aria-hidden="true">
      <div class="errpage__blob errpage__blob--a"></div>
      <div class="errpage__blob errpage__blob--b"></div>
      <div class="errpage__blob errpage__blob--c"></div>
    </div>

    <main class="errpage__card">
      <div class="errpage__art" aria-hidden="true">
        <slot name="art" />
      </div>

      <div class="errpage__code">{{ code }}</div>
      <h1 class="errpage__title">{{ title }}</h1>
      <p class="errpage__desc">{{ description }}</p>

      <div class="errpage__actions">
        <button type="button" class="errpage__btn errpage__btn--primary" @click="goPrimary">
          {{ primaryLabel }}
        </button>
        <button type="button" class="errpage__btn errpage__btn--ghost" @click="goBack">
          Go back
        </button>
      </div>

      <div v-if="hint" class="errpage__hint">{{ hint }}</div>
    </main>

    <footer class="errpage__foot">
      <span>Manakkal OMS</span>
      <span class="errpage__sep" aria-hidden="true">·</span>
      <span>{{ code }} error</span>
    </footer>
  </div>
</template>

<script>
import { isAuthenticated } from '@/auth/access'

export default {
  name: 'ErrorPageLayout',
  props: {
    code: { type: [String, Number], required: true },
    title: { type: String, required: true },
    description: { type: String, default: '' },
    hint: { type: String, default: '' },
    // Visual tone — drives the gradient palette behind the card.
    //  'brand'  — green (default, 404)
    //  'amber'  — warm amber (403, forbidden / locked)
    //  'crimson'— red (500, system error)
    tone: {
      type: String,
      default: 'brand',
      validator: (v) => ['brand', 'amber', 'crimson'].includes(v),
    },
  },
  computed: {
    isLoggedIn() {
      return isAuthenticated()
    },
    primaryLabel() {
      return this.isLoggedIn ? 'Go to dashboard' : 'Go to sign in'
    },
  },
  methods: {
    goPrimary() {
      this.$router.push({ path: this.isLoggedIn ? '/dashboard' : '/login' })
    },
    goBack() {
      // history.length === 1 means a direct hit on this URL (no prior
      // entry to go back to). Fall back to the home redirect so the
      // button never feels dead.
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.goPrimary()
    },
  },
}
</script>

<style scoped>
.errpage {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 1.5rem;
  background: #f6f8fb;
  font-family: inherit;
  overflow: hidden;
}

/* ── Background blobs ───────────────────────────────────── */
.errpage__bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.errpage__blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.55;
  animation: errpage-drift 18s ease-in-out infinite;
}

.errpage__blob--a {
  width: 340px;
  height: 340px;
  top: -80px;
  left: -80px;
}

.errpage__blob--b {
  width: 440px;
  height: 440px;
  bottom: -160px;
  right: -120px;
  animation-delay: -6s;
}

.errpage__blob--c {
  width: 260px;
  height: 260px;
  top: 40%;
  left: 55%;
  animation-delay: -12s;
  opacity: 0.35;
}

.errpage--brand .errpage__blob--a { background: radial-gradient(circle at 30% 30%, #16a07a, transparent 70%); }
.errpage--brand .errpage__blob--b { background: radial-gradient(circle at 60% 40%, #0e5b46, transparent 70%); }
.errpage--brand .errpage__blob--c { background: radial-gradient(circle at 50% 50%, #34d399, transparent 70%); }

.errpage--amber .errpage__blob--a { background: radial-gradient(circle at 30% 30%, #f59e0b, transparent 70%); }
.errpage--amber .errpage__blob--b { background: radial-gradient(circle at 60% 40%, #b45309, transparent 70%); }
.errpage--amber .errpage__blob--c { background: radial-gradient(circle at 50% 50%, #fbbf24, transparent 70%); }

.errpage--crimson .errpage__blob--a { background: radial-gradient(circle at 30% 30%, #ef4444, transparent 70%); }
.errpage--crimson .errpage__blob--b { background: radial-gradient(circle at 60% 40%, #991b1b, transparent 70%); }
.errpage--crimson .errpage__blob--c { background: radial-gradient(circle at 50% 50%, #fb7185, transparent 70%); }

@keyframes errpage-drift {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%      { transform: translate(40px, -30px) scale(1.08); }
  66%      { transform: translate(-30px, 40px) scale(0.95); }
}

/* ── Card ───────────────────────────────────────────────── */
.errpage__card {
  position: relative;
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 20px;
  padding: 2.4rem 2rem 2rem;
  text-align: center;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.12);
  animation: errpage-rise 0.5s cubic-bezier(0.34, 1.4, 0.64, 1) both;
}

@keyframes errpage-rise {
  0%   { transform: translateY(16px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
}

.errpage__art {
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
  animation: errpage-float 4.5s ease-in-out infinite;
}

@keyframes errpage-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}

.errpage__code {
  font-size: 3.2rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
  margin: 0.3rem 0 0.55rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  -webkit-background-clip: text;
          background-clip: text;
  color: transparent;
}

.errpage--amber .errpage__code {
  background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%);
  -webkit-background-clip: text;
          background-clip: text;
}

.errpage--crimson .errpage__code {
  background: linear-gradient(135deg, #991b1b 0%, #ef4444 100%);
  -webkit-background-clip: text;
          background-clip: text;
}

.errpage__title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0 0 0.45rem;
  letter-spacing: -0.01em;
}

.errpage__desc {
  font-size: 0.92rem;
  color: #6b7280;
  margin: 0 0 1.4rem;
  line-height: 1.55;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.errpage__actions {
  display: inline-flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 0.6rem;
}

.errpage__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  min-width: 150px;
  padding: 0 1.3rem;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.errpage__btn:focus { outline: none; }
.errpage__btn::-moz-focus-inner { border: 0; }
.errpage__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.30);
}

.errpage__btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.25);
}

.errpage--amber .errpage__btn--primary {
  background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%);
  box-shadow: 0 4px 12px rgba(180, 83, 9, 0.25);
}

.errpage--crimson .errpage__btn--primary {
  background: linear-gradient(135deg, #991b1b 0%, #ef4444 100%);
  box-shadow: 0 4px 12px rgba(153, 27, 27, 0.25);
}

.errpage__btn--primary:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 8px 18px rgba(14, 91, 70, 0.35);
}

.errpage__btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.errpage__btn--ghost:hover {
  background: #f8fafc;
  border-color: #d8dde6;
}

.errpage__hint {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 1rem;
}

.errpage__foot {
  position: relative;
  margin-top: 1.5rem;
  font-size: 0.75rem;
  color: #94a3b8;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  font-weight: 600;
}

.errpage__sep {
  color: #cbd5e1;
}

@media (max-width: 540px) {
  .errpage__card {
    padding: 1.8rem 1.3rem 1.5rem;
    border-radius: 16px;
  }

  .errpage__code {
    font-size: 2.5rem;
  }

  .errpage__title {
    font-size: 1.1rem;
  }

  .errpage__btn {
    width: 100%;
    min-width: 0;
  }
}
</style>
